import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { GlacierWaveProps } from '../../../interface';
import { getProductUrl } from '../../../const';
import { getTreaserAni } from '../../util';
import css from './index.module.scss';
import GlacierBox from './GlacierBox';
import WaveTwoBox from './WaveTwoBox';
import { useGlobal } from '@/utils/useGlobal';
import NextImage from '@/components/NextImage';
import EFAddIcon from '@/components/UI/svg/Icons/EFAddIcon';

const PRODUCT_KEYS = {
    glacier: 'GLACIER',
    waveTwo: 'WAVE2',
};

const TreaserC: FC<GlacierWaveProps> = (props) => {
    const { useLang, Link, isMobile, gtm, useRouter } = useGlobal();
    const { t } = useLang('glacierwave.treaser');
    const [currentKey, setCurrentKey] = useState('');
    const [isClient, setIsClient] = useState<boolean>(false);
    const [wrapperClsName, setWrapperClsName] = useState('');
    const [clsName, setClsName] = useState('');
    const router = useRouter();
    const { locale, query } = router;

    const productList = [
        {
            key: PRODUCT_KEYS.glacier,
            img: (
                <NextImage
                    webpSrc={require('@publicLocal/glacier-and-wave-2/pc/product-glacier.webp')}
                    src={require('@publicLocal/glacier-and-wave-2/pc/product-glacier.png')}
                />
            ),
            title: t('product_glacier'),
            list: [t('glacier_ice'), t('glacier_live_free'), t('glacier_get_hungry')],
            text:
                locale === 'au'
                    ? t.rich('portable_appliance', {
                          tag: (text: string) => <div className="content-text-icon">{text}</div>,
                      })
                    : '',
            author: locale === 'au' ? t('author_one') : '',
        },
        {
            key: PRODUCT_KEYS.waveTwo,
            img: (
                <NextImage
                    webpSrc={require('@publicLocal/glacier-and-wave-2/pc/product-wave-2.webp')}
                    src={require('@publicLocal/glacier-and-wave-2/pc/product-wave-2.png')}
                />
            ),
            title: t('product_wave_two'),
            list: [t('wave_fastest'), t('wave_running_time'), t('wave_anywhere')],
            text:
                locale === 'au'
                    ? t.rich('conditioning_device', {
                          tag: (text: string) => <div className="content-text-icon">{text}</div>,
                      })
                    : '',
            author: locale === 'au' ? t('author_two') : '',
        },
    ];

    // 隐藏滚动条
    useEffect(() => {
        setIsClient(true);
        document.documentElement.style.overflow = clsName === 'transIn' ? 'hidden' : '';
        return () => {
            document.documentElement.style.overflow = '';
        };
    }, [clsName]);

    useEffect(() => {
        const treaserAni = getTreaserAni();
        treaserAni?.create({
            syncFromTo: [
                [`.${css.glacierWaveTreaserC} .product-container-title`, { opacity: 0.1, ease: 'linear' }, { opacity: 1 }],
                [`.${css.glacierWaveTreaserC} .product-container`, { opacity: 0.1, ease: 'linear' }, { opacity: 1 }],
            ],
            triggerElement: `.${css.glacierWaveTreaserC}`,
            triggerHook: 1,
            offset: '15%',
            duration: 100,
        });
    }, []);

    const setBoxClosed = () => {
        setCurrentKey('');
        setClsName('transOut');
        setWrapperClsName('transOut');
    };
    const setBoxOpen = (productKey: string) => {
        setCurrentKey(productKey);
        setClsName('transIn');
        setWrapperClsName('transIn');
        gtm.push({
            event: 'uaEvent',
            event_name: 'select_product',
            list_name: 'Product Preview',
            product_name: `EcoFlow ${productKey}`,
            button_name: t('learn_more'),
        });
    };

    const getDataLayer = (type: string, productKey: string) => {
        return {
            event: 'uaEvent',
            event_name: 'select_product',
            list_name: 'Product Preview',
            product_name: `EcoFlow ${productKey}`,
            button_name: type,
        };
    };

    const getBtn = (productKey: string) => {
        const urlMap = getProductUrl(locale);
        if (locale === 'au') {
            const cid = query?.cid;
            return (
                <Link
                    href={
                        productKey === PRODUCT_KEYS.glacier
                            ? `/glacier-portable-refrigerator${cid ? `?cid=${cid}` : ''}`
                            : `/wave-2-portable-air-conditioner${cid ? `?cid=${cid}` : ''}`
                    }
                    target="_self"
                    dataLayer={getDataLayer(t('learn_more'), productKey)}
                >
                    <button role="button" className="more-btn">
                        {t('learn_more')}
                        <div className="add-icon">
                            <EFAddIcon />
                        </div>
                    </button>
                </Link>
            );
        }

        if (locale === 'kr') {
            const href_kr = productKey === PRODUCT_KEYS.glacier ? urlMap.glacierDetail : urlMap.wave2Detail;

            return (
                <Link href={href_kr} target="_blank" dataLayer={getDataLayer(t('learn_more'), productKey)}>
                    <button role="button" className="more-btn">
                        {t('learn_more')}
                        <div className="add-icon">
                            <EFAddIcon />
                        </div>
                    </button>
                </Link>
            );
        }

        switch (props.curStage) {
            case 3:
                const href3 = productKey === PRODUCT_KEYS.glacier ? urlMap.glacierShopify : urlMap.wave2Shopify;

                return (
                    <Link href={href3} target="_blank" dataLayer={getDataLayer(t('learn_more'), productKey)}>
                        <button role="button" className="more-btn">
                            {t('learn_more')}
                            <div className="add-icon">
                                <EFAddIcon />
                            </div>
                        </button>
                    </Link>
                );

            case 2:
                const href = productKey === PRODUCT_KEYS.glacier ? urlMap.glacierDetail : urlMap.wave2Detail;

                return (
                    <Link href={href} target="_blank" dataLayer={getDataLayer(t('learn_more'), productKey)}>
                        <button role="button" className="more-btn">
                            {t('learn_more')}
                            <div className="add-icon">
                                <EFAddIcon />
                            </div>
                        </button>
                    </Link>
                );
            default:
                if (locale === 'kr') {
                    const href = productKey === PRODUCT_KEYS.glacier ? urlMap.glacierDetail : urlMap.wave2Detail;

                    return (
                        <Link href={href} target="_blank" dataLayer={getDataLayer(t('learn_more'), productKey)}>
                            <button role="button" className="more-btn">
                                {t('learn_more')}
                                <div className="add-icon">
                                    <EFAddIcon />
                                </div>
                            </button>
                        </Link>
                    );
                }
                return (
                    <button role="button" className="more-btn" onClick={() => setBoxOpen(productKey)}>
                        {t('learn_more')}
                        <div className="add-icon">
                            <EFAddIcon />
                        </div>
                    </button>
                );
        }
    };

    return (
        <div className={`${css.glacierWaveTreaserC} ${wrapperClsName} devicesc-pc`}>
            <div className="product-container-title">{t('swiper_title')}</div>
            <div className="product-container">
                {productList.map((productItem) => (
                    <div className="product-intro-item" key={productItem.key}>
                        <div className="intro-img-box">{productItem.img}</div>
                        <div className="product-intro-title">{productItem.title}</div>
                        <ul className="product-intro-list">
                            {productItem.list.map((item, idx) => (
                                <li className="intro-list-item" key={idx}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                        {getBtn(productItem.key)}
                        {productItem?.text && productItem?.author && (
                            <div className="content-box">
                                <div className="content-text-wrapper">
                                    <span className="content-text">{productItem.text}</span>
                                </div>
                                <div className="content-author">{productItem.author}</div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="product-box-desc">{t('product_container_desc')}</div>
            {isClient &&
                createPortal(
                    <div className={`${css.carwinbox} card-win-show ${clsName} ${isMobile ? 'box-mobile' : 'box-pc'}`}>
                        {currentKey === PRODUCT_KEYS.glacier ? (
                            <GlacierBox handleBoxClose={() => setBoxClosed()} />
                        ) : (
                            <WaveTwoBox handleBoxClose={() => setBoxClosed()} />
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default TreaserC;
